import { Alert, LinearProgress } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { useAuth } from "@poseidon/ui/components/AuthContext";
import { useAction } from "../Action/useAction";
import * as api from "./api";
import * as React from "react";
import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import { camelToSentenceCase } from "@poseidon/utils/text";
import { singularize } from "@poseidon/utils/string";

const ACTION_REQUIRED = "actionRequired";
const PENDING_APPROVAL = "pendingApproval";
const COMPLETE = "complete";

function AlertDialog({ open, onConfirm, onCancel }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        Have you uploaded all the required files?
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          We have already saved any files you have added, so if you still need
          to add more, please add those first before continuing.
          <br />
          <br />
          This will submit to Salt to review so they can progress the
          application.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>No, let me add some more</Button>
        <Button onClick={onConfirm} autoFocus>
          Yes, submit files for review
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const getStateText = (state) => {
  if (ACTION_REQUIRED === state) {
    return "Action Required";
  }
  if (PENDING_APPROVAL === state) {
    return "Pending Approval";
  }
  if (COMPLETE === state) {
    return "Complete";
  }
};

const getSwitchState = ({ state }) => {
  if (ACTION_REQUIRED === state) {
    return {
      checked: false,
      disabled: false,
    };
  }

  if (PENDING_APPROVAL === state) {
    return {
      checked: true,
      disabled: false,
    };
  }

  if (COMPLETE === state) {
    return {
      checked: true,
      disabled: true,
    };
  }
};

const getText = ({ state, actionFor, note }) => {
  if (PENDING_APPROVAL === state) {
    return `Marked as complete by you. Pending review from Salt Finance.`;
  }
  if (COMPLETE === state) {
    return `Submitted and approved by Salt, thank you.`;
  }

  if ("admin" === actionFor) {
    return "Awaiting action from admin.";
  }
  const actor = getActionForFromUserReference(actionFor);
  if ("dealers" === actor) {
    return note
      ? `Salt have reviewed and reassigned to you with the message: ${note}`
      : "Awaiting action from you";
  }
  if ("sailors" === actor) {
    return "Awaiting action from sailor.";
  }
  // @todo: log error
  return "Action state not set, requires administration, please contact admin";
};

const getSeverity = ({ state }) => {
  if (PENDING_APPROVAL === state) {
    return "warning";
  }
  if (COMPLETE === state) {
    return "success";
  }
  return "error";
};

const getActionForFromUserReference = (reference) => {
  try {
    if ("" === reference) {
      return "";
    }
    if ("admin" === reference) {
      return "admin";
    }
    const [collection] = reference._key.path.segments.slice(-2);
    return collection;
  } catch (error) {
    console.error(
      "Couldn't read path in document reference, check value is valid document reference"
    );
    return "";
  }
};

export default function DealerAction({ entity, entityId, entityKey, sx }) {
  const { user } = useAuth();
  const { setToast } = useToastMessage();
  const { setError } = useErrorMessage();
  const { action, loading, error } = useAction({
    entity,
    entityId,
    entityKey,
  });

  const [confirmOpen, setConfirmOpen] = useState(false);

  const onConfirm = async () => {
    const state = "pendingApproval";
    const response = await api.setAction(
      entity,
      entityId,
      entityKey,
      state,
      user.uid
    );
    if (response instanceof Error) {
      setError(response.message);
    } else {
      setToast(`Updated status to ${getStateText(state)}`);
    }
    setConfirmOpen(false);
  };
  const onCancel = () => {
    setConfirmOpen(false);
  };

  if (error) {
    setError(error);
  }

  if (loading || !action) {
    return <LinearProgress />;
  }

  const actionForCurrentUser = action.actionFor?.id === user.uid;
  //todo: completedBy does not exist - should we use origin==='dealer'?
  const completedByCurrentUser = action.completedBy?.id === user.uid;

  if (actionForCurrentUser || completedByCurrentUser) {
    const { checked, disabled } = getSwitchState(action);

    return (
      <Stack spacing={2} sx={sx}>
        <Alert severity={getSeverity(action)}>{getText(action)}</Alert>
        {!disabled && (
          <Button
            variant="contained"
            color="primary"
            // todo: make confirm dialog etc assign back to dealer
            onClick={() => setConfirmOpen(true)}
          >
            {checked
              ? "I need to make some changes"
              : `${camelToSentenceCase(
                  singularize(entityKey)
                )} files are now complete, submit to Salt for approval`}
          </Button>
        )}
        <AlertDialog
          onCancel={onCancel}
          onConfirm={onConfirm}
          open={confirmOpen}
        />
      </Stack>
    );
  }

  return (
    <Alert severity={getSeverity(action)} sx={sx}>
      {getText(action)}
    </Alert>
  );
}
